import Grid from '@config/Grid/Grid'
import Tile from '@/shared/components/Tile/Tile'
import SidebarAccordion from '@/features/menu-management/SidebarAccordion/SidebarAccordion'
import { Outlet, useLocation } from 'react-router-dom'
import { Suspense, useEffect, useState } from 'react'
import LoadingPage from '@/pages/LoadingPage'
import { useTranslation } from 'react-i18next'
import styles from '@/features/menu-management/MenuManagement.module.scss'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { useMenuSidebarStore } from '@/shared/store/useMenuSidebarStore'
import { AreaChart } from '@/features/dashboard/Charts/AreaChart/AreaChart'
import { useDishesInsightsQuery } from '@/features/menu-management/Dishes/hooks/useDishesInsightsQuery'
import { useToppingsInsightsQuery } from '@/features/menu-management/Toppings/hooks/useToppingsInsightsQuery'
import { useIngredientsInsightsQuery } from '@/features/menu-management/Ingredients/hooks/useIngredientsInsightsQuery'
import {
  currencySymbol,
  formatPrice,
  minorToMajor
} from '@/shared/utils/price'

type dailySalesType = {
  date: string
  totalSales: number
}

type dailyCountsType = {
  date: string
  totalCount: number
}

type dailiesAveragesType = {
  date: string
  averageDaily: number
}

type cumulativeSalesType = {
  date: string
  totalSales: number
}

function MenuManagement() {
  const resetIsDirty = useFormDirtyStore(s => s.reset)
  const { t } = useTranslation()
  const [title, setTitle] = useState(t('DISHES_MANAGEMENT.TITLE'))
  const location = useLocation()
  const { isOpen, setIsOpen } = useMenuSidebarStore()

  // Open the settings sidebar on entry so the user doesn't have to find the toggle.
  useEffect(() => {
    setIsOpen(true)
  }, [setIsOpen])
  const first_start_date = '2025-10-09'
  const first_end_date = '2025-11-09'
  const second_start_date = '2025-10-01'
  const second_end_date = '2025-10-08'
  const ingredient_type_filter = 'count'

  const [dailySales, setDailySales] = useState<dailySalesType[]>([])
  const [dailyCounts, setDailyCounts] = useState<dailyCountsType[]>([])
  const [dailiesAverages, setDailiesAverages] = useState<dailiesAveragesType[]>(
    []
  )
  const [cumulativeSales, setCumulativeSales] = useState<cumulativeSalesType[]>(
    []
  )

  const [totalSalesValue, setTotalSalesValue] = useState(0)
  const [
    totalSalesValueComparisonPercentage,
    setTotalSalesValueComparisonPercentage
  ] = useState(0)

  const [totalSalesCount, setTotalSalesCount] = useState(0)
  const [
    totalSalesCountComparisonPercentage,
    setTotalSalesCountComparisonPercentage
  ] = useState(0)

  const [totalSalesAverage, setTotalSalesAverage] = useState(0)
  const [
    totalSalesAverageComparisonPercentage,
    setTotalSalesAverageComparisonPercentage
  ] = useState(0)

  const [
    totalCumulativeSalesComparisonPercentage,
    setTotalCumulativeSalesComparisonPercentage
  ] = useState(0)

  const {
    totalSalesAveragesDishes,
    totalSalesCountDishes,
    totalSalesCumulativeValueDishes,
    totalSalesValueDishes
  } = useDishesInsightsQuery({
    first_start_date,
    first_end_date,
    second_start_date,
    second_end_date
  })

  const {
    totalSalesAveragesToppings,
    totalSalesCountToppings,
    totalSalesCumulativeValueToppings,
    totalSalesValueToppings
  } = useToppingsInsightsQuery({
    first_start_date,
    first_end_date,
    second_start_date,
    second_end_date
  })

  const {
    totalSalesAveragesIngredients,
    totalSalesCountIngredients,
    totalSalesCumulativeValueIngredients,
    totalSalesValueIngredients
  } = useIngredientsInsightsQuery({
    first_start_date,
    first_end_date,
    second_start_date,
    second_end_date,
    ingredient_type_filter
  })

  useEffect(() => {
    if (location.pathname.includes('dishes')) {
      setTitle(t('DISHES_MANAGEMENT.TITLE'))
      setDailySales(totalSalesValueDishes?.dailySales || [])
      setDailyCounts(totalSalesCountDishes?.dailyCounts || [])
      setDailiesAverages(totalSalesAveragesDishes?.dailiesAverages || [])
      setCumulativeSales(totalSalesCumulativeValueDishes?.cumulativeSales || [])
      setTotalSalesValue(totalSalesValueDishes?.totalSales || 0)
      setTotalSalesValueComparisonPercentage(
        totalSalesValueDishes?.comparisonPercentage || 0
      )
      setTotalSalesCount(totalSalesCountDishes?.totalSalesCounts || 0)
      setTotalSalesCountComparisonPercentage(
        totalSalesCountDishes?.comparisonPercentage || 0
      )

      setTotalSalesAverage(totalSalesAveragesDishes?.averageTotal || 0)
      setTotalSalesAverageComparisonPercentage(
        totalSalesAveragesDishes?.comparisonPercentage || 0
      )

      setTotalCumulativeSalesComparisonPercentage(
        totalSalesCumulativeValueDishes?.growthTrendComparison || 0
      )
    }
    if (location.pathname.includes('toppings')) {
      setTitle(t('TOPPINGS_MANAGEMENT.TITLE'))
      setDailySales(totalSalesValueToppings?.dailySales || [])
      setDailyCounts(totalSalesCountToppings?.dailyCounts || [])
      setDailiesAverages(totalSalesAveragesToppings?.dailiesAverages || [])
      setCumulativeSales(
        totalSalesCumulativeValueToppings?.cumulativeSales || []
      )
      setTotalSalesValue(totalSalesValueToppings?.totalSales || 0)
      setTotalSalesValueComparisonPercentage(
        totalSalesValueToppings?.comparisonPercentage || 0
      )
      setTotalSalesCount(totalSalesCountToppings?.totalSalesCounts || 0)
      setTotalSalesCountComparisonPercentage(
        totalSalesCountToppings?.comparisonPercentage || 0
      )
      setTotalSalesAverage(totalSalesAveragesToppings?.averageTotal || 0)
      setTotalSalesAverageComparisonPercentage(
        totalSalesAveragesToppings?.comparisonPercentage || 0
      )

      setTotalCumulativeSalesComparisonPercentage(
        totalSalesCumulativeValueToppings?.growthTrendComparison || 0
      )
    }
    if (location.pathname.includes('ingredients')) {
      setTitle(t('INGREDIENTS_MANAGEMENT.TITLE'))
      setDailySales(totalSalesValueIngredients?.dailySales || [])
      setDailyCounts(totalSalesCountIngredients?.dailyCounts || [])
      setDailiesAverages(totalSalesAveragesIngredients?.dailiesAverages || [])
      setCumulativeSales(
        totalSalesCumulativeValueIngredients?.cumulativeSales || []
      )
      setTotalSalesValue(totalSalesValueIngredients?.totalSales || 0)
      setTotalSalesValueComparisonPercentage(
        totalSalesValueIngredients?.comparisonPercentage || 0
      )
      setTotalSalesCount(totalSalesCountIngredients?.totalSalesCounts || 0)
      setTotalSalesCountComparisonPercentage(
        totalSalesCountIngredients?.comparisonPercentage || 0
      )
      setTotalSalesAverage(totalSalesAveragesIngredients?.averageTotal || 0)
      setTotalSalesAverageComparisonPercentage(
        totalSalesAveragesIngredients?.comparisonPercentage || 0
      )
      setTotalCumulativeSalesComparisonPercentage(
        totalSalesCumulativeValueIngredients?.growthTrendComparison || 0
      )
    }
    if (location.pathname.includes('data-extraction')) {
      setTitle(t('DATA_EXTRACTION.TITLE'))
    }
  }, [
    location.pathname,
    t,
    totalSalesAveragesDishes?.averageTotal,
    totalSalesAveragesDishes?.comparisonPercentage,
    totalSalesAveragesDishes?.dailiesAverages,
    totalSalesAveragesIngredients?.averageTotal,
    totalSalesAveragesIngredients?.comparisonPercentage,
    totalSalesAveragesIngredients?.dailiesAverages,
    totalSalesAveragesToppings?.averageTotal,
    totalSalesAveragesToppings?.comparisonPercentage,
    totalSalesAveragesToppings?.dailiesAverages,
    totalSalesCountDishes?.comparisonPercentage,
    totalSalesCountDishes?.dailyCounts,
    totalSalesCountDishes?.totalSalesCounts,
    totalSalesCountIngredients?.comparisonPercentage,
    totalSalesCountIngredients?.dailyCounts,
    totalSalesCountIngredients?.totalSalesCounts,
    totalSalesCountToppings?.comparisonPercentage,
    totalSalesCountToppings?.dailyCounts,
    totalSalesCountToppings?.totalSalesCounts,
    totalSalesCumulativeValueDishes?.cumulativeSales,
    totalSalesCumulativeValueDishes?.growthTrendComparison,
    totalSalesCumulativeValueIngredients?.cumulativeSales,
    totalSalesCumulativeValueIngredients?.growthTrendComparison,
    totalSalesCumulativeValueToppings?.cumulativeSales,
    totalSalesCumulativeValueToppings?.growthTrendComparison,
    totalSalesValueDishes?.comparisonPercentage,
    totalSalesValueDishes?.dailySales,
    totalSalesValueDishes?.totalSales,
    totalSalesValueIngredients?.comparisonPercentage,
    totalSalesValueIngredients?.dailySales,
    totalSalesValueIngredients?.totalSales,
    totalSalesValueToppings?.comparisonPercentage,
    totalSalesValueToppings?.dailySales,
    totalSalesValueToppings?.totalSales
  ])

  useEffect(() => resetIsDirty(), [resetIsDirty])

  const tableContainerClassName = isOpen ? 'start-2 col-8' : 'start-2 col-11'
  const sideBarAccordionClassName = isOpen ? 'start-10 col-3' : 'hidden'

  const description = location.pathname.includes('toppings')
    ? t('MENU_MANAGEMENT.DESCRIPTION.TOPPINGS')
    : location.pathname.includes('ingredients')
      ? t('MENU_MANAGEMENT.DESCRIPTION.INGREDIENTS')
      : location.pathname.includes('data-extraction')
        ? ''
        : t('MENU_MANAGEMENT.DESCRIPTION.DISHES')

  const totalSalesChartDescription = (
    <div className={styles.chartsDescriptionContainer}>
      <div className={styles.chartsDescription}>
        <span className="title-bold-large">
          {formatPrice(totalSalesValue, { withCurrency: false })}
        </span>
        <span className="title-small">{` ${currencySymbol()}`}</span>
      </div>
      <div className={styles.percentageChangeIndicator}>
        <div
          className={`${styles.percentageChangeIndicatorArrowWrapper} ${
            totalSalesValueComparisonPercentage < 0 ? styles.negativeValue : ''
          }`}
        >
          {totalSalesValueComparisonPercentage < 0 ? (
            <i className="las la-arrow-down icon-medium" />
          ) : (
            <i className="las la-arrow-up icon-medium" />
          )}
        </div>
        <span className="title-small">
          {totalSalesValueComparisonPercentage.toFixed(2)}%
        </span>
      </div>
    </div>
  )

  const totalOrdersChartDescription = (
    <div className={styles.chartsDescriptionContainer}>
      <div className={styles.chartsDescription}>
        <span className="title-bold-large">{totalSalesCount.toFixed(0)}</span>
      </div>
      <div className={styles.percentageChangeIndicator}>
        <div
          className={`${styles.percentageChangeIndicatorArrowWrapper} ${
            totalSalesCountComparisonPercentage < 0 ? styles.negativeValue : ''
          }`}
        >
          {totalSalesCountComparisonPercentage < 0 ? (
            <i className="las la-arrow-down icon-medium" />
          ) : (
            <i className="las la-arrow-up icon-medium" />
          )}
        </div>
        <span className="title-small">
          {totalSalesCountComparisonPercentage.toFixed(2)}%
        </span>
      </div>
    </div>
  )

  const averageSalesChartDescription = (
    <div className={styles.chartsDescriptionContainer}>
      <div className={styles.chartsDescription}>
        <span className="title-bold-large">
          {formatPrice(totalSalesAverage, { withCurrency: false })}
        </span>
        <span className="title-small">{` ${currencySymbol()}`}</span>
      </div>
      <div className={styles.percentageChangeIndicator}>
        <div
          className={`${styles.percentageChangeIndicatorArrowWrapper} ${
            totalSalesAverageComparisonPercentage < 0
              ? styles.negativeValue
              : ''
          }`}
        >
          {totalSalesAverageComparisonPercentage < 0 ? (
            <i className="las la-arrow-down icon-medium" />
          ) : (
            <i className="las la-arrow-up icon-medium" />
          )}
        </div>
        <span className="title-small">
          {totalSalesAverageComparisonPercentage.toFixed(2)}%
        </span>
      </div>
    </div>
  )

  const cumulativeSalesChartDescription = (
    <div className={styles.chartsDescriptionContainer}>
      <div className={styles.percentageChangeIndicator}>
        <div
          className={`${styles.percentageChangeIndicatorArrowWrapper} ${
            totalCumulativeSalesComparisonPercentage < 0
              ? styles.negativeValue
              : ''
          }`}
        >
          {totalCumulativeSalesComparisonPercentage < 0 ? (
            <i className="las la-arrow-down icon-medium" />
          ) : (
            <i className="las la-arrow-up icon-medium" />
          )}
        </div>
        <span className="title-small">
          {totalCumulativeSalesComparisonPercentage.toFixed(2)}%
        </span>
      </div>
    </div>
  )

  // Charts plot major units, the API delivers every amount in minor ones.
  const totalSalesChartData = [
    {
      dataKey: 'totalSales',
      chartData: dailySales.map(item => ({
        ...item,
        totalSales: minorToMajor(item.totalSales).toString()
      }))
    }
  ]
  const totalOrdersChartData = [
    {
      dataKey: 'totalCount',
      chartData: dailyCounts.map(item => ({
        ...item,
        totalCount: item.totalCount.toString()
      }))
    }
  ]
  const averageSalesChartData = [
    {
      dataKey: 'averageDaily',
      chartData: dailiesAverages.map(item => ({
        ...item,
        averageDaily: minorToMajor(item.averageDaily).toString()
      }))
    }
  ]
  const cumulativeSalesChartData = [
    {
      dataKey: 'totalSales',
      chartData: cumulativeSales.map(item => ({
        ...item,
        totalSales: minorToMajor(item.totalSales).toString()
      }))
    }
  ]

  return (
    <Grid>
      <div
        className={`${styles.menuManagementTableContainer} ${tableContainerClassName}`}
      >
        <Tile color="black">
          <div className={styles.tileInner}>
            <div id="tour-menu-content">
              <p className={`title-bold-medium ${styles.title}`}>{title}</p>
              {description && (
                <p className={`body-medium ${styles.description}`}>
                  {description}
                </p>
              )}
            </div>
            {!location.pathname.includes('data-extraction') && (
              <div className={styles.chartsWrapper}>
                <AreaChart
                  title={t('DISHES_MANAGEMENT.TOTAL_SALES')}
                  chartsData={totalSalesChartData}
                  description={totalSalesChartDescription}
                  condensed
                  isLoading={false}
                  loadingPlaceholder={<div />}
                />
                <AreaChart
                  title={t('DISHES_MANAGEMENT.TOTAL_ORDERS')}
                  chartsData={totalOrdersChartData}
                  description={totalOrdersChartDescription}
                  condensed
                  isLoading={false}
                  loadingPlaceholder={<div />}
                />
                <AreaChart
                  title={t('DISHES_MANAGEMENT.AVERAGE_SALES')}
                  chartsData={averageSalesChartData}
                  description={averageSalesChartDescription}
                  condensed
                  isLoading={false}
                  loadingPlaceholder={<div />}
                />
                <AreaChart
                  title={t('DISHES_MANAGEMENT.CUMULATIVE_SALES')}
                  chartsData={cumulativeSalesChartData}
                  description={cumulativeSalesChartDescription}
                  condensed
                  isLoading={false}
                  loadingPlaceholder={<div />}
                />
              </div>
            )}
            <div className={styles.outletWrapper}>
              <Suspense fallback={<LoadingPage />}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </Tile>
      </div>
      <div className={`${sideBarAccordionClassName} align-start`}>
        <Tile color="black" customPadding="0 0.5rem 0.5rem 0.5rem">
          <SidebarAccordion />
        </Tile>
      </div>
    </Grid>
  )
}

export default MenuManagement
